'use client';

import { useEffect, useState } from 'react';

import {
  type ClaimDecision,
  interpretClaimResult,
} from '~/lib/release-celebrations/claim-result';
import type { RunningRelease } from '~/lib/release-celebrations/running-release';

/** Let the page settle before claiming, so the notice never lands mid-paint. */
export const CLAIM_SETTLE_MS = 1_200;
/** Never START a claim later than this after the app became eligible. */
export const CLAIM_WINDOW_MS = 10_000;

export type ClaimFn = (release: RunningRelease) => Promise<unknown>;

export type ShownDecision = Extract<ClaimDecision, { show: true }> & {
  release: RunningRelease;
};

/**
 * At most one claim per app start (a full page load). Client-side navigation
 * never claims again; a reload is a new app start, and the server answers
 * `seen` for a release already handled.
 *
 * The claim lives at module scope, not in the component, so leaving the app
 * routes while it is in flight (the server may already have receipted it)
 * does not lose the answer: the next mount adopts it, inside the window or
 * after it. Once a notice has been handed to a component it is never handed
 * out again, so a dismissed notice does not come back on a later remount.
 */
type StartupClaim = {
  openedAt: number;
  result: Promise<ClaimDecision> | null;
  delivered: boolean;
};

const appStartClaims = new Map<string, StartupClaim>();

/** Tests only. */
export function resetReleaseClaimForTests() {
  appStartClaims.clear();
}

/**
 * A claim that already succeeded must still be shown if the tab was hidden.
 * No startup deadline: the receipt is already written, so dropping the answer
 * means that release never appears.
 */
function waitUntilVisible(signal: AbortSignal): Promise<boolean> {
  if (signal.aborted) return Promise.resolve(false);
  if (document.visibilityState === 'visible') return Promise.resolve(true);
  return new Promise((resolve) => {
    const finish = (ready: boolean) => {
      document.removeEventListener('visibilitychange', check);
      signal.removeEventListener('abort', onAbort);
      resolve(ready);
    };
    const onAbort = () => finish(false);
    const check = () => {
      if (document.visibilityState === 'visible') finish(true);
    };
    document.addEventListener('visibilitychange', check);
    signal.addEventListener('abort', onAbort);
  });
}

function waitForVisiblePage(
  signal: AbortSignal,
  deadline: number,
): Promise<boolean> {
  if (signal.aborted || Date.now() >= deadline) {
    return Promise.resolve(false);
  }
  if (
    document.readyState === 'complete' &&
    document.visibilityState === 'visible'
  ) {
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    let loaded = document.readyState === 'complete';
    const finish = (ready: boolean) => {
      clearTimeout(timeout);
      window.removeEventListener('load', onLoad);
      document.removeEventListener('visibilitychange', check);
      signal.removeEventListener('abort', check);
      resolve(ready);
    };
    const check = () => {
      if (signal.aborted || Date.now() >= deadline) {
        finish(false);
      } else if (loaded && document.visibilityState === 'visible') {
        finish(true);
      }
    };
    const onLoad = () => {
      loaded = true;
      check();
    };
    const timeout = setTimeout(check, Math.max(0, deadline - Date.now()));
    window.addEventListener('load', onLoad);
    document.addEventListener('visibilitychange', check);
    signal.addEventListener('abort', check);
    check();
  });
}

/**
 * Runs the claim; never rejects. No timeout on purpose: the server writes the
 * receipt as soon as the request arrives, so abandoning a slow answer would
 * consume the release with no notice. A slow answer is shown when it comes.
 */
async function runClaim(
  claim: ClaimFn,
  release: RunningRelease,
): Promise<ClaimDecision> {
  try {
    return interpretClaimResult(await claim(release));
  } catch {
    // Offline, RPC missing: show nothing; the app carries on.
    return { show: false };
  }
}

/**
 * PAY-4392: claims the running release once, inside the safe startup window,
 * and says whether to show the notice.
 *
 * The window opens when the app becomes eligible (signed-in app route, flag
 * on, a known release) and closes {@link CLAIM_WINDOW_MS} later: if the page
 * is still not loaded by then, nothing is claimed and nothing is shown. The
 * claim starts only once the page has loaded and settled, i.e. when the
 * client is ready to render it. A claim that has started is always seen
 * through, because the server has already receipted it.
 *
 * Any failure (offline, RPC missing, unexpected payload) shows nothing, and
 * the app carries on.
 */
export function useReleaseCelebrationClaim({
  eligible,
  userId,
  release,
  claim,
}: {
  eligible: boolean;
  userId: string;
  release: RunningRelease | null;
  claim: ClaimFn;
}): ShownDecision | null {
  const [decision, setDecision] = useState<{
    userId: string;
    value: ShownDecision;
  } | null>(null);

  useEffect(() => {
    if (!eligible || !release) return;

    let startup = appStartClaims.get(userId);
    if (!startup) {
      startup = { openedAt: Date.now(), result: null, delivered: false };
      appStartClaims.set(userId, startup);
    }
    const ownedStartup = startup;
    const deadline = ownedStartup.openedAt + CLAIM_WINDOW_MS;
    const waiting = new AbortController();

    const deliver = async (result: ClaimDecision) => {
      if (!result.show || ownedStartup.delivered) return;
      if (document.visibilityState !== 'visible') {
        if (!(await waitUntilVisible(waiting.signal))) return;
      }
      if (waiting.signal.aborted || ownedStartup.delivered) return;
      ownedStartup.delivered = true;
      setDecision({ userId, value: { ...result, release } });
    };

    const settle = setTimeout(
      async () => {
        // A claim this app start already made is delivered whenever it
        // finishes, window or not: the receipt is written, so dropping the
        // answer would lose the release for good.
        if (!ownedStartup.result) {
          if (!(await waitForVisiblePage(waiting.signal, deadline))) return;
          if (waiting.signal.aborted || Date.now() >= deadline) return;
          ownedStartup.result ??= runClaim(claim, release);
        }
        await deliver(await ownedStartup.result);
      },
      Math.max(0, ownedStartup.openedAt + CLAIM_SETTLE_MS - Date.now()),
    );

    return () => {
      clearTimeout(settle);
      waiting.abort();
    };
  }, [eligible, userId, release, claim]);

  return eligible && decision?.userId === userId ? decision.value : null;
}
